"use client";

import { useEffect, useReducer } from "react";

import formatRelativeTime from "@/utils/formatRelativeTime";
import { cn } from "@/lib/utils";

/**
 * Live-updating relative time label.
 *
 * Calling `Date.now()` inside a Server Component throws under Next.js 16
 * Cache Components prerendering. Pushing the time read into a Client
 * Component is the documented escape hatch — Next.js excludes Client
 * Components from the prerendered HTML for the time-dependent portion, and
 * the value can refresh in-place without re-rendering the server tree.
 *
 * Reference: https://nextjs.org/docs/messages/next-prerender-current-time
 */
interface RelativeTimeProps {
	/** ISO date string (e.g. `created_at` from Supabase). */
	when: string;
	/** Optional className applied to the rendered <span>. */
	className?: string;
	/**
	 * Optional fallback rendered before the client tick has run (and during
	 * server prerender). Defaults to an empty string so the layout reserves
	 * space but doesn't flash a stale value.
	 */
	fallback?: string;
}

function getNextTickMs(when: string): number | null {
	const ts = new Date(when).getTime();
	if (Number.isNaN(ts)) return null;

	const diffMs = Date.now() - ts;
	const diffSec = Math.floor(diffMs / 1000);
	const diffMin = Math.floor(diffSec / 60);
	const diffHours = Math.floor(diffMin / 60);

	if (diffSec < 60) return 5_000;
	if (diffMin < 60) return 30_000;
	if (diffHours < 24) return 5 * 60_000;
	return 24 * 60 * 60_000;
}

export function RelativeTime({
	when,
	className,
	fallback = "",
}: RelativeTimeProps) {
	const [, tick] = useReducer(() => ({}), {});

	const formatted = formatRelativeTime(when);

	// Recursive `setTimeout` so each tick reschedules at a cadence appropriate
	// to the *current* elapsed time (e.g. seconds → 5s ticks, minutes → 30s,
	// hours → 5min, days → 24h). `useReducer`'s dispatch is stable so the
	// effect's deps stay clean.
	useEffect(() => {
		let timeoutId: number | undefined;

		const schedule = () => {
			const nextTickMs = getNextTickMs(when);
			if (nextTickMs == null) return;
			timeoutId = window.setTimeout(() => {
				tick();
				schedule();
			}, nextTickMs);
		};

		schedule();

		return () => {
			if (timeoutId != null) window.clearTimeout(timeoutId);
		};
	}, [when]);

	return (
		<span className={cn(className, "")} suppressHydrationWarning>
			{formatted || fallback}
		</span>
	);
}

export default RelativeTime;
